import * as React from "react";
import axios from "axios";
import Header from "../../components/Header";
import SimpleTable from "../../components/SimpleTable";
import {
  Box,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Divider,
  TextField,
  MenuItem,
  useTheme,
  InputAdornment,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { tokens } from "../../theme";
import PermissionGuard from "../../components/PermissionGuard";

/* ---------- helpers ---------- */
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : "-");
const show = (v) =>
  v === null || v === undefined || v === "" ? "-" : String(v).trim();
const toNum = (v) =>
  v === "" || v === null || v === undefined ? "" : Number(v);

// normalize option rows from any API shape into {id, name}
const normalizeList = (list, nameKeys = ["name", "fullName", "title"]) => {
  if (!Array.isArray(list)) return [];
  return list
    .map((o) => {
      const id =
        o?.id ?? o?.ID ?? o?.Id ?? o?.itemId ?? o?.warehouseId ?? o?.uuid;
      const key = nameKeys.find((k) => o && o[k] != null);
      const name = o?.[key] ?? o?.label ?? o?.code ?? `#${id ?? "?"}`;
      return { id, name };
    })
    .filter((o) => o.id !== undefined && o.id !== null);
};

// keep current ID selectable even if not in fetched list
function ensureOption(options, id) {
  if (id === "" || id === null || id === undefined) return options || [];
  const nid = Number(id);
  const exists = (options || []).some((o) => Number(o.id) === nid);
  return exists ? options : [{ id: nid, name: `#${nid}` }, ...(options || [])];
}

/* ---------- API ---------- */
const token = localStorage.getItem("token");
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 20000,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  
  },
});

async function getJSON(path, params) {
  try {
    const res = await api.get(path, {
      params: { _t: Date.now(), ...(params || {}) },
    });
    return { data: res.data?.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}
async function putJSON(path, body) {
  try {
    const res = await api.put(path, body, {
      
    });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message;
    return { data: null, error: msg };
  }
}
async function deleteJSON(path) {
  try {
    const res = await api.delete(path, {
      
    });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message;
    return { data: null, error: msg };
  }
}

/* ---------- page ---------- */
export default function ViewStore() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [selectedWarehouse, setSelectedWarehouse] = React.useState("");

  // table data & ui
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [toast, setToast] = React.useState({
    open: false,
    msg: "",
    sev: "success",
  });

  // dialogs
  const [detailRow, setDetailRow] = React.useState(null);

  const [editOpen, setEditOpen] = React.useState(false);
  const [editRow, setEditRow] = React.useState(null);
  const [editValues, setEditValues] = React.useState({
    itemId: "",
    warehouseId: "",
    quantity: "",
  });
  const [saving, setSaving] = React.useState(false);

  const [deleteRow, setDeleteRow] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  // dropdown options
  const [items, setItems] = React.useState([]);
  const [warehouses, setWarehouses] = React.useState([]);
  const [optsLoading, setOptsLoading] = React.useState(false);

  // fetch list (cache-busted)
  const load = React.useCallback(async (warehouseId) => {
    setLoading(true);
    const params = warehouseId ? { warehouseId } : {};
    const { data, error } = await getJSON("/api/store", params);
    setLoading(false);

    if (error) {
      setToast({
        open: true,
        msg: "Failed to load store records",
        sev: "error",
      });
      setRows([]);
      return;
    }
    setRows(Array.isArray(data) ? data : []);
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  // fetch options for dropdowns (cache-busted)
  const loadOptions = React.useCallback(async () => {
    setOptsLoading(true);
    const [itemRes, whRes] = await Promise.all([
      getJSON("/api/item"),
      getJSON("/api/warehouse"),
    ]);
    setOptsLoading(false);

    setItems(normalizeList(itemRes.data)); // {id,name}
    setWarehouses(normalizeList(whRes.data)); // {id,name}
  }, []);

  React.useEffect(() => {
    // fetch once on mount
    loadOptions();
  }, [loadOptions]);

  // columns to display in table
  const columns = [
    {
      field: "no",
      headerName: "No.",
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      valueGetter: (params) => {
        // index among visible rows on the current page
        const visibleIndex = params.api.getRowIndexRelativeToVisibleRows(
          params.id,
        );
        // page & pageSize for absolute numbering across pages
        const pm = params.api?.state?.pagination?.paginationModel || {
          page: 0,
          pageSize: rows.length || 0,
        };
        const pageOffset = (pm.page || 0) * (pm.pageSize || 0);
        return pageOffset + visibleIndex + 1;
      },
    },
    { field: "id", headerName: "ID", width: 80, hide: true },
    {
      field: "item",
      headerName: "Item",
      flex: 1,
      minWidth: 180,
      valueGetter: (p) => p.row.item?.name ?? "-",
    },
    {
      field: "warehouse",
      headerName: "Warehouse",
      flex: 1,
      minWidth: 180,
      valueGetter: (p) => p.row.warehouse?.name ?? "-",
    },
    { field: "quantity", headerName: "Quantity", width: 120 },

    {
      field: "actions",
      headerName: "Actions",
      width: 170,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row;
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Details">
              <IconButton size="small" onClick={() => setDetailRow(row)}>
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip>
              <PermissionGuard module="Store" action="update">
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={async () => {
                  setEditRow(row);
                  setEditValues({
                    itemId: toNum(row.itemId),
                    warehouseId: toNum(row.warehouseId),
                    quantity: row.quantity ?? "",
                  });
                  // ensure latest options before opening dialog
                  await loadOptions();
                  setEditOpen(true);
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            </PermissionGuard>
            <PermissionGuard module="Store" action="delete">
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteRow(row)}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
            </PermissionGuard>
          </Box>
        );
      },
    },
  ];

  // grid look (theme-consistent)
  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": {
      backgroundColor: colors.primary[400],
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
    "& .MuiCheckbox-root": {
      color: `${colors.greenAccent[200]} !important`,
    },
    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
      color: `${colors.grey[100]} !important`,
    },
  };

  // update (optimistic + refetch)
  const handleUpdate = async () => {
    if (!editRow?.id) return;
    setSaving(true);

    const payload = {
      itemId: Number(editValues.itemId),
      warehouseId: Number(editValues.warehouseId),
      quantity: Number(editValues.quantity),
    };

    const { data, error } = await putJSON(`/api/store/${editRow.id}`, payload);
    setSaving(false);

    if (error) {
      setToast({ open: true, msg: "Update failed", sev: "error" });
      return;
    }

    // PUT returns { message, data: {updatedObj} } in your sample
    const updated = data?.data || data;
    const merged = updated?.id ? updated : { id: editRow.id, ...payload };

    setRows((prev) =>
      prev.map((r) => (r.id === editRow.id ? { ...r, ...merged } : r)),
    );

    setToast({
      open: true,
      msg: data?.message || "Store updated successfully",
      sev: "success",
    });
    setEditOpen(false);
    setEditRow(null);

    // refresh store + dropdown options so names are current
    await Promise.all([load(), loadOptions()]);
  };

  // delete (optimistic + refetch)
  const handleDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);

    const { data, error } = await deleteJSON(`/api/store/${deleteRow.id}`);
    setDeleting(false);

    if (error) {
      setToast({ open: true, msg: "Delete failed", sev: "error" });
      return;
    }

    setRows((prev) => prev.filter((r) => r.id !== deleteRow.id));

    setToast({
      open: true,
      msg: data?.message || "Store record deleted successfully",
      sev: "success",
    });
    setDeleteRow(null);

    await Promise.all([load(), loadOptions()]);
  };

  return (
    <>
      <Box m="20px">
        <Header
          title="STORE"
          subtitle="View, edit, and manage store inventory"
        />

        <Box m="40px 0 0 0">
          <Box mb={2} sx={{ maxWidth: 300 }}>
            <TextField
              label="Filter by Warehouse"
              select
              fullWidth
              value={selectedWarehouse}
              onChange={async (e) => {
                const value = e.target.value;
                setSelectedWarehouse(value);
                await load(value); // fetch filtered data
              }}
              variant="outlined"
            >
              <MenuItem value="">All Warehouses</MenuItem>
              {warehouses.map((w) => (
                <MenuItem key={w.id} value={Number(w.id)}>
                  {w.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <SimpleTable
            rows={rows}
            columns={columns}
            loading={loading}
            height="75vh"
            getRowId={(r) => r.id}
            sx={gridSx}
          />
        </Box>
      </Box>

      {/* Details */}
      <Dialog
        open={!!detailRow}
        onClose={() => setDetailRow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Store Details</DialogTitle>
        <DialogContent dividers>
          {detailRow && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <LabelValue label="Quantity" value={show(detailRow.quantity)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LabelValue label="Item" value={detailRow.item?.name ?? "-"} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LabelValue
                  label="Warehouse"
                  value={detailRow.warehouse?.name ?? "-"}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Store Record</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
            {/* Item dropdown + refresh */}
            <TextField
              label={
                optsLoading
                  ? "Loading items..."
                  : items.length
                    ? "Item"
                    : "No items found (refresh)"
              }
              select
              value={editValues.itemId}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, itemId: toNum(e.target.value) }))
              }
              fullWidth
              variant="filled"
              disabled={optsLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Refresh items">
                      <span>
                        <IconButton
                          size="small"
                          onClick={loadOptions}
                          disabled={optsLoading}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            >
              {ensureOption(items, editValues.itemId).map((it) => (
                <MenuItem key={it.id} value={Number(it.id)}>
                  {it.name ?? `#${it.id}`}
                </MenuItem>
              ))}
            </TextField>

            {/* Warehouse dropdown + refresh */}
            <TextField
              label={
                optsLoading
                  ? "Loading warehouses..."
                  : warehouses.length
                    ? "Warehouse"
                    : "No warehouses found (refresh)"
              }
              select
              value={editValues.warehouseId}
              onChange={(e) =>
                setEditValues((s) => ({
                  ...s,
                  warehouseId: toNum(e.target.value),
                }))
              }
              fullWidth
              variant="filled"
              disabled={optsLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Refresh warehouses">
                      <span>
                        <IconButton
                          size="small"
                          onClick={loadOptions}
                          disabled={optsLoading}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            >
              {ensureOption(warehouses, editValues.warehouseId).map((w) => (
                <MenuItem key={w.id} value={Number(w.id)}>
                  {w.name ?? `#${w.id}`}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Quantity"
              type="number"
              value={editValues.quantity}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, quantity: e.target.value }))
              }
              fullWidth
              variant="filled"
              inputProps={{ step: "1", min: 0 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={saving}
            variant="contained"
            color="secondary"
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!deleteRow}
        onClose={() => setDeleteRow(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Store Record</DialogTitle>
        <DialogContent dividers>
          <Typography>Are you sure you want to delete it?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            color="error"
            variant="contained"
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}

/* ---------- subcomponents ---------- */
function LabelValue({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {value ?? "-"}
      </Typography>
      <Divider sx={{ mt: 1 }} />
    </Box>
  );
}
